/** @format */
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useActiveFeatureFlags } from "posthog-js/react";
import React from "react";
import { useForm } from "react-hook-form";

import { resetPassword, useUser } from "@/api/userAPI";
import { type ResetPasswordFormData, resetPasswordSchema } from "@/lib/schemas";
import { setFormErrorsFromApi } from "@/lib/utils/formErrorHandling";

const ResetPassword = ({
  setModalComponent,
  setModalOpen,
}: {
  setModalComponent: (component: string | null) => void;
  setModalOpen: (open: boolean) => void;
}) => {
  const [success, setSuccess] = React.useState(false);
  const flags = useActiveFeatureFlags();
  const { data: { email } = {} } = useUser();
  const navigate = useNavigate();

  // Redirect if login feature flag is not enabled
  React.useEffect(() => {
    if (!flags?.includes("login")) {
      void navigate({ to: "/", replace: true });
    }
  }, [flags, navigate]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: email ?? "",
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      await resetPassword(data.email);
      setSuccess(true);
    } catch (error: unknown) {
      // Use the centralized form error handling utility
      setFormErrorsFromApi(error, setError);
      console.error("Password reset request error:", error);
    }
  };

  if (!flags?.includes("login")) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="self-center content-center bg-slate-900 rounded-xl p-4 w-full max-w-2xl">
      <h1 className="text-xl mb-2">Reset Password</h1>
      {success ? (
        <div className="text-green-700">
          <p>
            If an account with that email exists, you will receive an email with a link
            to reset your password.
          </p>
          <button
            type="button"
            onClick={() => {
              setModalComponent(null);
              setModalOpen(false);
            }}
            className="mt-2 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={e => void handleSubmit(onSubmit)(e)} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-200">
              E-Mail
            </label>
            <div className="mt-1">
              <input
                type="email"
                id="email"
                autoComplete="email"
                required
                {...register("email")}
                className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
              />
            </div>
            {errors.email && (
              <div className="text-red-700 whitespace-pre-wrap">
                {errors.email.message}
              </div>
            )}
          </div>
          <div>
            <button
              type="submit"
              disabled={isSubmitting || Object.keys(errors).length > 0}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed"
            >
              Reset Password
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
